import axios from 'axios';

export interface ParsedApiError {
  /** Lines to show to the user, e.g. 'genInfo.genComp should not be empty' */
  messages: string[];
  /** Form field name -> message, for formik.setFieldError */
  fieldErrors: Record<string, string>;
}

interface ApiErrorFallbacks {
  generic: string;
  network: string;
}

// Aggregated message emitted by RequiredServiceFieldsConstraint on the backend
const SERVICE_FIELDS_PREFIX = 'Missing required fields for the selected services:';

// 'genInfo.genComp should not be empty', 'company must be a string',
// 'ccpInfo.ccpBankAcq is required'
const CONSTRAINT_PATTERN = /^(?:([A-Za-z]\w*)\.)?([A-Za-z]\w*) (?:should|must|is)\b/;

function toMessageList(payload: any): string[] {
  const message = payload?.message;

  if (Array.isArray(message)) {
    return message.filter((entry: unknown): entry is string => typeof entry === 'string');
  }
  if (typeof message === 'string') {
    return [message];
  }
  return [];
}

/** Turns the aggregated 'missing fields' message back into one line per field. */
function expandServiceFieldsMessage(message: string): string[] {
  if (!message.startsWith(SERVICE_FIELDS_PREFIX)) {
    return [message];
  }

  return message
    .slice(SERVICE_FIELDS_PREFIX.length)
    .split(',')
    .map((path) => path.trim())
    .filter(Boolean)
    .map((path) => `${path} is required`);
}

/**
 * Reads a failed request into something the forms can display. Validation
 * messages carry the offending field, so they are also handed back keyed by
 * the flat field name the precheck forms use.
 */
export function parseApiError(error: unknown, fallbacks: ApiErrorFallbacks): ParsedApiError {
  if (!axios.isAxiosError(error)) {
    return { messages: [fallbacks.generic], fieldErrors: {} };
  }

  if (!error.response) {
    return { messages: [fallbacks.network], fieldErrors: {} };
  }

  const messages = toMessageList(error.response.data).flatMap(expandServiceFieldsMessage);

  if (messages.length === 0) {
    return { messages: [fallbacks.generic], fieldErrors: {} };
  }

  const fieldErrors: Record<string, string> = {};
  messages.forEach((message) => {
    const match = CONSTRAINT_PATTERN.exec(message);
    // The forms keep every field at the top level, so the group prefix the
    // backend reports (genInfo.genComp) is dropped here.
    const field = match?.[2];
    if (field && !fieldErrors[field]) {
      fieldErrors[field] = message;
    }
  });

  return { messages, fieldErrors };
}
